import React, { useState, useEffect, useCallback, useMemo } from "react";
import MyRegistrationCard from "./MyRegistrationCard";
import { getMyRegistrations, getEventForRegistration } from "../../hooks/useRegistration";
import "./MyRegistrations.css";
import { useNotification } from "../../context/NotificationContext";
import useRegistrationSearch from "../../hooks/useRegistirationSearch";
import { EventRegistration, RegistrationStatus } from "../../types/shared.types";
import Pagination from "../shared/Pagination";
import SearchBar from "../shared/SearchBar";
import Loading from "../shared/Loading";

const MyRegistrations: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [searchResults, setSearchResults] = useState<EventRegistration[] | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [eventNameMap, setEventNameMap] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<RegistrationStatus | "all">(
    "all",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { setNotification } = useNotification();

  const statusOptions = useMemo(
    () => [
      { label: "All Statuses", value: "all" },
      { label: "Confirmed", value: RegistrationStatus.Registered },
      { label: "Tentative", value: RegistrationStatus.Tentative },
      { label: "Declined", value: RegistrationStatus.Declined },
      { label: "Attended", value: RegistrationStatus.Attended },
      { label: "Cancelled", value: RegistrationStatus.Cancelled },
    ],
    [],
  );

  const fetchRegistrations = useCallback(async () => {
    setIsLoading(true);

    const response = await getMyRegistrations();

    if (response.success && response.data) {
      setRegistrations(response.data);

      // Build eventId → eventName map for search
      const uniqueEventIds = [...new Set(response.data.map((r) => r.eventId))];
      const nameMap: Record<string, string> = {};
      await Promise.all(
        uniqueEventIds.map(async (eventId) => {
          const eventRes = await getEventForRegistration(eventId);
          if (eventRes.success && eventRes.data) {
            nameMap[eventId] = eventRes.data.eventName;
          }
        }),
      );
      setEventNameMap(nameMap);
    } else {
      setNotification({
        type: "error",
        message: response.error || "Failed to load your registrations",
      });
    }

    setIsLoading(false);
  }, [setNotification]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const handleUpdateSuccess = (updatedRegistration: EventRegistration) => {
    setRegistrations((prev) =>
      prev.map((r) =>
        r.registrationId === updatedRegistration.registrationId
          ? updatedRegistration
          : r,
      ),
    );
    setSearchResults((prev) =>
      prev
        ? prev.map((r) =>
            r.registrationId === updatedRegistration.registrationId
              ? updatedRegistration
              : r,
          )
        : null,
    );
    setNotification({
      type: "success",
      message: "Registration updated successfully!",
    });
  };

  const handleCancelSuccess = (registrationId: string) => {
    setRegistrations((prev) =>
      prev.map((r) =>
        r.registrationId === registrationId
          ? { ...r, status: RegistrationStatus.Cancelled }
          : r,
      ),
    );
    setSearchResults((prev) =>
      prev
        ? prev.map((r) =>
            r.registrationId === registrationId
              ? { ...r, status: RegistrationStatus.Cancelled }
              : r,
          )
        : null,
    );
    setNotification({
      type: "success",
      message: "Registration cancelled successfully!",
    });
  };

  const activeRegistrations = searchResults ?? registrations;

  const filteredRegistrations = activeRegistrations.filter(
    (reg) => statusFilter === "all" || reg.status === statusFilter,
  );

  const totalItems = filteredRegistrations.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;

  const statusOrder = ['Registered', 'Tentative', 'Declined', 'Attended', 'Cancelled'];
  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => 
    statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
  );

  const paginatedRegistrations = sortedRegistrations.slice(startIdx, endIdx);

  const groupedRegistrations = {
    confirmed: paginatedRegistrations.filter(
      (r) => r.status === RegistrationStatus.Registered,
    ),
    tentative: paginatedRegistrations.filter(
      (r) => r.status === RegistrationStatus.Tentative,
    ),
    attended: paginatedRegistrations.filter(
      (r) => r.status === RegistrationStatus.Attended,
    ),
    cancelled: paginatedRegistrations.filter(
      (r) =>
        r.status === RegistrationStatus.Cancelled ||
        r.status === RegistrationStatus.Declined,
    ),
  };

  const { handleSearch } = useRegistrationSearch(
    setSearchResults,
    registrations,
    () => setCurrentPage(1),
    eventNameMap,
  );

  const titleText = `Registrations (${filteredRegistrations.length})`;

  useEffect(() => {
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, itemsPerPage]);

  if (isLoading) {
    return <Loading message="Loading my registrations..." onRetry={fetchRegistrations} />;
  }

  return (
    <div className="my-registrations-page">
      <div className="my-registrations-container">
        {registrations.length === 0 ? (
          <div className="empty-state">
            <h3>No Registrations Found</h3>
            <p>You haven't registered for any events yet.</p>
          </div>
        ) : (
          <>
            <div className="filters-section">
              <SearchBar
                title={titleText}
                placeholder="Search by event name"
                searchQuery={searchQuery}
                onSearchQueryChange={(e) => setSearchQuery(e.target.value)}
                onSearch={() => handleSearch(searchQuery)}
              />
              <select
                value={String(statusFilter)}
                onChange={(e) =>
                  setStatusFilter(e.target.value as RegistrationStatus | "all")
                }
                className="filter-select"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {filteredRegistrations.length === 0 ? (
              <div className="empty-state">
                <h3>No Registrations Found</h3>
                <p>No registrations match your filter criteria.</p>
              </div>
            ) : (
              <>
                {groupedRegistrations.confirmed.length > 0 && (
                  <div className="registration-section">
                    <div className="registrations-grid">
                      {groupedRegistrations.confirmed.map((registration) => (
                        <MyRegistrationCard
                          key={registration.registrationId}
                          registration={registration}
                          onUpdateSuccess={handleUpdateSuccess}
                          onCancelSuccess={handleCancelSuccess}
                          showQRCode={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedRegistrations.tentative.length > 0 && (
                  <div className="registration-section">
                    <p className="section-hint">
                      Please confirm or decline these before the event deadline.
                    </p>
                    <div className="registrations-grid">
                      {groupedRegistrations.tentative.map((registration) => (
                        <MyRegistrationCard
                          key={registration.registrationId}
                          registration={registration}
                          onUpdateSuccess={handleUpdateSuccess}
                          onCancelSuccess={handleCancelSuccess}
                          showQRCode={false}
                          highlightAction={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedRegistrations.attended.length > 0 && (
                  <div className="registration-section">
                    <div className="registrations-grid">
                      {groupedRegistrations.attended.map((registration) => (
                        <MyRegistrationCard
                          key={registration.registrationId}
                          registration={registration}
                          onUpdateSuccess={handleUpdateSuccess}
                          onCancelSuccess={handleCancelSuccess}
                          showQRCode={false}
                          isReadOnly={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedRegistrations.cancelled.length > 0 && (
                  <div className="registration-section">
                    <div className="registrations-grid">
                      {groupedRegistrations.cancelled.map((registration) => (
                        <MyRegistrationCard
                          key={registration.registrationId}
                          registration={registration}
                          onUpdateSuccess={handleUpdateSuccess}
                          onCancelSuccess={handleCancelSuccess}
                          showQRCode={false}
                          isReadOnly={true}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {filteredRegistrations.length > 0 && (
          <div style={{marginTop: "-20px"}}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              setCurrentPage={setCurrentPage}
              startIdx={startIdx}
              endIdx={endIdx}
              totalItems={filteredRegistrations.length}
            />
          </div>
        )}
      </div>         
    </div>
  );
};

export default MyRegistrations;
